import { useValueNarratives } from '../../../ValueNarrativesView/useValueNarratives'
import React, { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { DialogContent, SvgIcon } from '@mui/material'
import { SelectInput } from '../../../../shared/components/SelectInput'
import { ReactComponent as CloseIcon } from '../../../../assets/images/Close.svg'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import useValueMessageDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useValueMessageDisplayName'
import useValueNarrativeDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useValueNarrativeDisplayName'
import Button from '@mui/material/Button'
import { useTransferValueMessageModalContext } from '../../providers/TransferValueMessageModalProvider'
import { useTransferValueMessageMutation } from '../../queries/useTransferValueMessageMutation'

const ValueMessageTransferModal = () => {
    const { valueMessages, isOpen, handleClose } = useTransferValueMessageModalContext()

    const {
        isCategoriesDataReady,
        dataWithoutPseudoCategories,
        selectedCategory,
        selectedNarrative,
        handleSelectNarrative,
        handleSelectCategory,
    } = useValueNarratives()

    const categories = dataWithoutPseudoCategories.sort((a, b) => a.order - b.order)

    const { mutateAsync, isLoading } = useTransferValueMessageMutation({
        onTransferValueMessage: () => {
            handleClose()

            // Redirect to the narrative in the new category
            if (selectedNarrative?._id) {
                handleSelectNarrative(selectedCategoryId, narrativeId, true)
                return
            }

            // Redirect to the category if no narrative is selected
            if (selectedCategory?._id && !selectedCategory?.isPseudoCategory) {
                handleSelectCategory(selectedCategoryId)
                return
            }
        },
    })

    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
    const [narrativeId, setNarrativeId] = useState<string | null>(null)

    useEffect(() => {
        setSelectedCategoryId(null)
    }, [isOpen])

    const handleSave = async () => {
        await mutateAsync({ valueMessages, narrativeId, selectedNarrativeId: selectedNarrative?._id })
    }

    const canSave = !!selectedCategoryId && !isLoading && isCategoriesDataReady && valueMessages.length

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            fullWidth
            aria-labelledby="form-dialog-title"
            maxWidth="md"
            PaperProps={{
                sx: {
                    py: 4,
                    px: 8,
                    borderRadius: '24px',
                    boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
                },
            }}
        >
            <DialogTitle id="form-dialog-title" sx={{ p: 0 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    pb={1.5}
                    borderBottom="1px solid"
                    borderColor="figma.grayscale.stroke"
                >
                    <Typography variant="22x600" color="figma.grayscale.textHier1">
                        Transferring <strong>{useValueMessageDisplayName().plural}</strong>
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            p: 0.5,
                            color: 'figma.grayscale.textHier2',
                        }}
                    >
                        <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Stack direction="column" gap={3} py={3}>
                    <Stack direction="row" gap={1}>
                        <Typography variant="16x500" color="figma.grayscale.textHier1">
                            Transferring from: {selectedCategory?.name}
                        </Typography>
                        <Typography variant="16x700" color="figma.grayscale.textHier1"></Typography>
                    </Stack>
                    <Stack direction="column" gap={1}>
                        <Typography variant="16x500" color="figma.grayscale.textHier1">
                            Transferring to:
                        </Typography>
                        <Stack direction="column" width="50%" spacing={2}>
                            <SelectInput
                                required
                                selected={selectedCategoryId}
                                onChange={setSelectedCategoryId}
                                options={categories?.map((category) => ({
                                    id: category._id,
                                    label: category.name,
                                }))}
                                placeholder="Select Category"
                            />

                            <SelectInput
                                required
                                selected={narrativeId}
                                disabled={!selectedCategoryId}
                                onChange={setNarrativeId}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            width: 300,
                                            overflowX: 'auto',
                                        },
                                    },
                                }}
                                options={
                                    categories
                                        ?.find((cat) => cat._id === selectedCategoryId)
                                        ?.narratives?.map((narr) => ({
                                            id: narr._id,
                                            label: narr.name,
                                        }))
                                        ?.filter((narr) => narr.id !== selectedNarrative._id) ?? []
                                }
                                placeholder={'Select ' + useValueNarrativeDisplayName().singular}
                            />
                            <Stack>
                                <Typography> </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                    <Stack direction="column" gap={1.5}>
                        <Typography variant="16x400" color="figma.grayscale.textHier1">
                            Selected {useValueMessageDisplayName().plural}:
                        </Typography>
                        {valueMessages?.map((msg, ind) => (
                            <Typography>
                                {`${ind + 1}`}. {msg.name}{' '}
                            </Typography>
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Box flex={1} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        onClick={handleClose}
                        // @ts-expect-error mui type
                        variant="outlined-sm-button"
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!canSave}
                        onClick={handleSave}
                        // @ts-expect-error mui type
                        variant="contained-sm-button"
                        color="primary"
                        sx={{
                            '&.Mui-disabled': {
                                color: 'figma.primary2',
                            },
                        }}
                    >
                        Transfer
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}

export default ValueMessageTransferModal
